// Helpers for the backend's simple-query metadata grammar: a query is
// space-separated `key:value` clauses, ANDed together server-side.

// Mirrors the backend grammar exactly: keys are [a-zA-Z_]+; values are bare
// word characters, or double-quoted strings whose only escape is \" and
// whose characters exclude bare quotes and backslashes.
const METADATA_CLAUSE_RE = /^[a-zA-Z_]+:(?:"(?:\\"|[^"\\])*"|\w+)$/;

// Builds one clause of the grammar. Bare values must be word characters
// only; anything else has to be double-quoted with inner quotes escaped.
// Keys outside [a-zA-Z_]+ cannot be expressed at all.
export function metadataClause(key: string, value: string): string {
  return /^\w+$/.test(value)
    ? `${key}:${value}`
    : `${key}:"${value.replace(/"/g, '\\"')}"`;
}

export function isFilterableMetadataKey(key: string): boolean {
  return /^[a-zA-Z_]+$/.test(key);
}

// The grammar's only quoted-value escape is \" — a literal backslash cannot
// be expressed at all, so values containing one cannot be filtered on.
export function isFilterableMetadataValue(value: string): boolean {
  return !value.includes("\\");
}

// Splits a query into space-separated tokens, keeping quoted spans (with
// \" escapes) atomic so quoted values may contain spaces.
function tokenize(query: string): string[] {
  const tokens: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < query.length; i += 1) {
    const char = query[i]!;
    if (char === " " && !inQuotes) {
      if (current) {
        tokens.push(current);
      }
      current = "";
      continue;
    }
    if (char === "\\" && inQuotes && query[i + 1] === '"') {
      current += '\\"';
      i += 1;
      continue;
    }
    if (char === '"') {
      inQuotes = !inQuotes;
    }
    current += char;
  }
  if (current) {
    tokens.push(current);
  }
  return tokens;
}

// Splits a metadata query into whole clauses, keeping quoted values intact:
// `env:prod region:"us east"` -> ["env:prod", 'region:"us east"'].
// All-or-nothing, mirroring the backend parser: if any token is malformed
// the whole query is unparseable and [] is returned — never partial clauses.
export function metadataFilterClauses(query: string): string[] {
  const tokens = tokenize(query);
  return tokens.length > 0 &&
    tokens.every((token) => METADATA_CLAUSE_RE.test(token))
    ? tokens
    : [];
}

// Extracts just the keys from a metadata query for compact display in the
// filter chip: `env:prod region:"us east"` -> ["env", "region"].
export function metadataFilterKeys(query: string): string[] {
  return metadataFilterClauses(query).map((clause) =>
    clause.slice(0, clause.indexOf(":"))
  );
}
